import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { recruitNowWebhookQueryKeys } from "./queryKeys";
import type { PaginatedWebhookRegistrationsResponse } from "./useClientWebhookRegistrations";

import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";

interface ProblemDetails {
  detail?: string;
  title?: string;
  status?: number;
}

interface DeregisterWebhookContext {
  previousInfiniteData: InfiniteData<PaginatedWebhookRegistrationsResponse> | undefined;
  previousPaginatedData: PaginatedWebhookRegistrationsResponse | undefined;
}

export function useDeregisterRecruitNowWebhook() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<
    void,
    AxiosError<ProblemDetails>,
    { clientId: string; registrationId: string },
    DeregisterWebhookContext
  >({
    mutationFn: ({ clientId, registrationId }) =>
      instance.post(
        `/api/v2/Webhooks/RecruitNow/Clients/${clientId}/Registrations/${registrationId}/Deregister`
      ),
    onMutate: async ({ clientId, registrationId }) => {
      const infiniteQueryKeyBase = recruitNowWebhookQueryKeys.clientRegistrationsInfinite(clientId);
      const paginatedQueryKeyBase = recruitNowWebhookQueryKeys.clientRegistrations(clientId);

      // Cancel all queries matching the base keys (including those with excludeId)
      await queryClient.cancelQueries({ queryKey: infiniteQueryKeyBase, exact: false });
      await queryClient.cancelQueries({ queryKey: paginatedQueryKeyBase, exact: false });

      // Find all infinite query keys that match (including those with excludeId)
      // Store previous data for rollback
      // Use predicate to match queries with same clientId regardless of excludeId
      const queryCache = queryClient.getQueryCache();
      const allInfiniteQueries = queryCache.findAll({
        predicate: (query) => {
          const key = query.queryKey;
          // Match: ["recruitNowWebhooks", "clientRegistrationsInfinite", clientId, ...]
          return (
            Array.isArray(key) &&
            key.length >= 3 &&
            key[0] === "recruitNowWebhooks" &&
            key[1] === "clientRegistrationsInfinite" &&
            key[2] === clientId
          );
        },
      });
      const allPaginatedQueries = queryCache.findAll({
        predicate: (query) => {
          const key = query.queryKey;
          // Match: ["recruitNowWebhooks", "clientRegistrations", clientId, ...]
          return (
            Array.isArray(key) &&
            key.length >= 3 &&
            key[0] === "recruitNowWebhooks" &&
            key[1] === "clientRegistrations" &&
            key[2] === clientId
          );
        },
      });

      const previousInfiniteData = allInfiniteQueries.length > 0
        ? queryClient.getQueryData<InfiniteData<PaginatedWebhookRegistrationsResponse>>(allInfiniteQueries[0].queryKey)
        : undefined;
      const previousPaginatedData = allPaginatedQueries.length > 0
        ? queryClient.getQueryData<PaginatedWebhookRegistrationsResponse>(allPaginatedQueries[0].queryKey)
        : undefined;

      // Update all infinite queries (including those with excludeId)
      allInfiniteQueries.forEach((query) => {
        const currentData = queryClient.getQueryData<InfiniteData<PaginatedWebhookRegistrationsResponse>>(query.queryKey);
        if (currentData?.pages) {
          queryClient.setQueryData<InfiniteData<PaginatedWebhookRegistrationsResponse>>(
            query.queryKey,
            {
              ...currentData,
              pages: currentData.pages.map((page) => ({
                ...page,
                items: page.items.filter((reg) => reg.id !== registrationId),
                totalCount: Math.max(0, page.totalCount - 1),
              })),
            }
          );
        }
      });

      // Update all paginated queries
      allPaginatedQueries.forEach((query) => {
        const currentData = queryClient.getQueryData<PaginatedWebhookRegistrationsResponse>(query.queryKey);
        if (currentData) {
          queryClient.setQueryData<PaginatedWebhookRegistrationsResponse>(
            query.queryKey,
            {
              ...currentData,
              items: currentData.items.filter((reg) => reg.id !== registrationId),
              totalCount: Math.max(0, currentData.totalCount - 1),
            }
          );
        }
      });

      return {
        previousInfiniteData,
        previousPaginatedData,
      };
    },
    onError: (error, variables, context) => {
      // Rollback all queries that were optimistically updated
      const queryCache = queryClient.getQueryCache();
      const allInfiniteQueries = queryCache.findAll({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key.length >= 3 &&
            key[0] === "recruitNowWebhooks" &&
            key[1] === "clientRegistrationsInfinite" &&
            key[2] === variables.clientId
          );
        },
      });
      const allPaginatedQueries = queryCache.findAll({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key.length >= 3 &&
            key[0] === "recruitNowWebhooks" &&
            key[1] === "clientRegistrations" &&
            key[2] === variables.clientId
          );
        },
      });

      // Rollback to previous data for the first query (we only stored one for simplicity)
      // In practice, if there are multiple queries, they'll be refetched on invalidation
      if (context?.previousInfiniteData && allInfiniteQueries.length > 0) {
        queryClient.setQueryData(allInfiniteQueries[0].queryKey, context.previousInfiniteData);
      }
      if (context?.previousPaginatedData && allPaginatedQueries.length > 0) {
        queryClient.setQueryData(allPaginatedQueries[0].queryKey, context.previousPaginatedData);
      }

      const errorMessage =
        error.response?.data?.detail || t("webhooks.recruitnow.deregisterError");
      showToast(errorMessage, "error");
    },
    onSuccess: () => {
      showToast(t("webhooks.recruitnow.deregisterSuccess"), "success");
    },
    onSettled: async (_, __, variables) => {
      if (variables) {
        // Invalidate webhook configuration query in case deregistering clears it
        queryClient.invalidateQueries({
          queryKey: recruitNowWebhookQueryKeys.configuration(variables.clientId),
        });
        // Invalidate all infinite queries (including those with excludeId)
        queryClient.invalidateQueries({
          queryKey: recruitNowWebhookQueryKeys.clientRegistrationsInfinite(variables.clientId),
          exact: false, // Match all queries with this prefix, regardless of excludeId
        });
        // Invalidate all paginated queries
        queryClient.invalidateQueries({
          queryKey: recruitNowWebhookQueryKeys.clientRegistrations(variables.clientId),
          exact: false, // Match all queries with this prefix
        });
      }
      // Invalidate overview query to ensure it refetches when user navigates back
      queryClient.invalidateQueries({
        queryKey: recruitNowWebhookQueryKeys.overview(),
        refetchType: "all", // Refetch all matching queries, including inactive ones
      });
    },
  });
}
